import { Button, Table } from 'antd'
import React, { useCallback } from 'react'
import { createUseStyles, useTheme } from 'react-jss'
import moment from 'moment-timezone'
import { Link } from 'react-router-dom'
import { PageHeader } from '../Common'
import { useAsync } from '../../hooks'
import useQueryParams from '../../hooks/useQueryParams'
import { toPath } from '../../routes'
import {
  useTransmissions,
  useTransmissionsPagination,
  useFetchTransmissions,
} from '../../redux/slices/transmissions'
import Filters, { FIELDS_OPTIONS } from '../Forms/Filters'
import { useIsAdmin, useIsPractitioner } from '../../redux/slices/session'
import UnmatchedCount from './UnmatchedCount'

const useStyles = createUseStyles({
  disabledRow: { opacity: 0.5 },
})

const DEFAULT_FILTERS = {
  query: '',
  matched: FIELDS_OPTIONS.matched[0].id,
  showReviewed: 'false',
  deviceModelManufacturer: FIELDS_OPTIONS.deviceModelManufacturer[0].id,
  medicalProviderId: undefined,
  officeLocationId: undefined,
}
const QUERY_PARAMS_SCHEMA = { page: Number, showReviewed: Boolean }

function formatDateOfBirth(date) {
  if (typeof date === 'undefined') {
    return ''
  }
  return moment(date).format('L')
}

export default function TransmissionsList() {
  const isAdmin = useIsAdmin()
  const theme = useTheme()
  const classes = useStyles({ theme })
  const isPractitioner = useIsPractitioner()
  const [conditions, setConditions] = useQueryParams({
    initialValues: DEFAULT_FILTERS,
    schema: QUERY_PARAMS_SCHEMA,
  })

  const columns = [
    {
      title: 'Patient',
      dataIndex: 'patient.lastName',
      render(_text, transmission) {
        return (
          transmission.patient && (
            <Link to={toPath.patients.patient({ id: transmission.patient.id })}>
              {transmission.patient.fullName}
            </Link>
          )
        )
      },
    },
    {
      title: 'MRN',
      dataIndex: 'patient.mrn',
    },
    {
      title: 'DOB',
      dataIndex: 'patient.dateOfBirth',
      key: 'patient.dateOfBirth',
      render: formatDateOfBirth,
    },
    {
      title: 'Device Type',
      dataIndex: 'device.type',
    },
    {
      title: 'Serial Number',
      dataIndex: 'device.serialNumber',
    },
    {
      title: 'Transmission Date',
      key: 'transmissionDate',
      dataIndex: 'transmissionDate',
      render(text) {
        return moment(text).format('MM/DD/YYYY')
      },
    },
    {
      title: 'Practice',
      dataIndex: 'practice.name',
    },
    {
      title: 'View',
      ket: 'view',
      render(_text, transmission) {
        return (
          <Link to={toPath.transmissions.transmission({ id: transmission.id })}>
            <Button type="link" icon="file-search" />
          </Link>
        )
      },
    },
  ]

  const { pending } = useAsync(useFetchTransmissions(conditions))

  const handlePageChange = useCallback(
    page => setConditions({ ...conditions, page }),
    [conditions, setConditions],
  )

  const formatBirthDate = bd =>
    bd ? moment(bd).format('YYYY-MM-DD') : undefined

  const handleSubmit = useCallback(
    ({
      practiceId,
      deviceTypeId,
      deviceModelManufacturer,
      showReviewed,
      matched,
      query,
      birthDate,
      medicalProviderId,
      officeLocationId,
    }) => {
      setConditions({
        page: 1,
        practiceId,
        deviceTypeId,
        deviceModelManufacturer,
        matched,
        showReviewed,
        query,
        birthDate: formatBirthDate(birthDate),
        medicalProviderId,
        officeLocationId,
      })
    },
    [setConditions],
  )

  const rowClassName = useCallback(
    transmission => (transmission.isReviewed ? classes.disabledRow : undefined),
    [classes.disabledRow],
  )

  const { pageSize, total } = useTransmissionsPagination()
  const transmissions = useTransmissions()

  return (
    <div>
      <PageHeader
        title="Transmissions"
        extra={[
          <Link key="upload" to={toPath.practices.uploads()}>
            <Button>Upload transmission</Button>
          </Link>,
        ]}
      />
      {isAdmin && conditions.matched !== 'false' && <UnmatchedCount />}
      {(isAdmin || isPractitioner) && (
        <Filters
          currentValues={conditions}
          onSubmit={handleSubmit}
          showPractice={isAdmin}
          showDeviceType
          showSearch
          showBirthDate
          showMatchStatus
          showDeviceManufacturer
          showReviewedCheck
          showOfficeLocation
          showMedicalProvider
          placeholders={{
            query: 'Search by name, serial number or practice MRN',
          }}
        />
      )}

      <Table
        columns={columns}
        dataSource={transmissions}
        loading={pending}
        pagination={{
          current: conditions.page || 1,
          pageSize,
          onChange: handlePageChange,
          total,
        }}
        rowKey="id"
        rowClassName={rowClassName}
      />
    </div>
  )
}
